/**
 * Shop-wide review carousel. Judge.me stays the data backend via ARP.
 * Do not import this module from the PDP widget graph — theme ESM is not
 * tree-shaken.
 */
import { MysaReviewsHost } from "@theme/mysa-reviews-host";
import { fetchAllReviewsPage } from "@theme/mysa-reviews-arp";
import { bindReviewCard } from "@theme/mysa-reviews-card";

const DEFAULT_MIN_RATING = 4;

/** Judge.me Reviews Carousel – Legacy shows 15 reviews, not the whole ARP page. */
const DEFAULT_SLIDE_LIMIT = 15;

/** Product-scoped carousels filter the shop-wide feed, so fetch a deeper page. */
const SCOPED_FETCH_SIZE = "60";

/**
 * @param {string} value Comma-separated product handles
 * @returns {string[]}
 */
const parseHandles = (value) =>
  value
    .split(",")
    .map((handle) => handle.trim())
    .filter(Boolean);

/**
 * @param {string | undefined} productUrl
 * @param {string[]} handles
 */
const matchesProductHandle = (productUrl, handles) => {
  const match = /\/products\/([^/?#]+)/.exec(productUrl || "");
  return Boolean(match && handles.includes(match[1]));
};

/**
 * Featured (3-up) and quote carousels. Highest-rating ARP, rating >= 4,
 * at most 15 slides (Judge.me Reviews Carousel – Legacy cap). Optional
 * `data-product-handles` keeps only reviews linking to those products.
 * @extends {MysaReviewsHost}
 */
class MysaReviewsCarousel extends MysaReviewsHost {
  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  /**
   * Section morph restores empty slider markup. Refetch; beginLoad aborts
   * any in-flight ARP from before the morph.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#load();
  }

  /**
   * Host aborts in-flight ARP and disconnects the mutation observer.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  async #load() {
    if (!this.reviewsCdnEnabled()) return;
    const { token, signal } = this.beginLoad();
    const inner = this.#sliderInner();
    if (!inner) return;

    this.setHostStatus(this.dataset.i18nLoading || "", {
      screenReaderOnly: true,
    });

    const handles = parseHandles(this.dataset.productHandles || "");

    try {
      const payload = await fetchAllReviewsPage({
        signal,
        shopDomain: this.dataset.shopDomain || "",
        page: 1,
        perPage: handles.length
          ? SCOPED_FETCH_SIZE
          : this.dataset.perPage || String(DEFAULT_SLIDE_LIMIT),
        locale: this.dataset.locale || "en",
        sortBy: "highest-rating",
      });
      if (!this.isCurrentLoad(token)) return;

      const minRating = Number(this.dataset.minRating) || DEFAULT_MIN_RATING;
      const limit = Number(this.dataset.perPage) || DEFAULT_SLIDE_LIMIT;
      const translations = payload.translations || {};
      const fragment = document.createDocumentFragment();
      for (const review of payload.reviews) {
        if ((Number(review.rating) || 0) < minRating) continue;
        if (
          handles.length &&
          !matchesProductHandle(review.product_url, handles)
        )
          continue;
        const item = bindReviewCard(this, review, translations);
        if (!item) continue;
        fragment.append(item);
        if (fragment.childElementCount >= limit) break;
      }

      inner.replaceChildren(fragment);
      this.setHostStatus("");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (!this.isCurrentLoad(token)) return;
      this.logLoadError("Failed to load review carousel", error);
      if (inner.children.length === 0) {
        this.setHostStatus(this.dataset.i18nLoadError || "");
      } else {
        this.setHostStatus("");
      }
    }
  }

  /**
   * Slider inner lives on nested `mysa-slider`, not this host's refs map.
   * @returns {HTMLElement | null}
   */
  #sliderInner() {
    const inner = this.querySelector(".slider-inner");
    return inner instanceof HTMLElement ? inner : null;
  }
}

if (!customElements.get("mysa-reviews-carousel")) {
  customElements.define("mysa-reviews-carousel", MysaReviewsCarousel);
}
